/**
 * Reusable Card component for trAInedup.ai
 * Provides consistent styling for content containers
 */

import React from 'react';

interface CardProps extends React.HTMLAttributes<HTMLDivElement> {
  children: React.ReactNode;
  className?: string;
  padding?: 'none' | 'sm' | 'md' | 'lg';
  shadow?: 'none' | 'sm' | 'md' | 'lg';
  border?: boolean;
  hover?: boolean;
  onClick?: () => void;
}

/**
 * Card component with customizable padding, shadow, and hover effects
 * Follows trAInedup.ai design system
 */
export const Card: React.FC<CardProps> = ({
  children,
  className = '',
  padding = 'md',
  shadow = 'md',
  border = true,
  hover = false,
  onClick,
  ...props
}) => {
  // Base card classes
  const baseClasses = 'bg-white rounded-lg transition-all duration-200 max-w-full';

  // Padding classes
  const paddingClasses = {
    none: '',
    sm: 'p-4',
    md: 'p-6',
    lg: 'p-8',
  };

  // Shadow classes
  const shadowClasses = {
    none: '',
    sm: 'shadow-sm',
    md: 'shadow-md',
    lg: 'shadow-lg',
  };

  // Border classes
  const borderClasses = border ? 'border border-neutral-200' : '';

  // Hover classes
  const hoverClasses = hover ? 'hover:shadow-lg hover:-translate-y-1' : '';

  // Interactive classes
  const interactiveClasses = onClick ? 'cursor-pointer' : '';

  // Combine all classes
  const cardClasses = `${baseClasses} ${paddingClasses[padding]} ${shadowClasses[shadow]} ${borderClasses} ${hoverClasses} ${interactiveClasses} ${className}`;

  return (
    <div 
      className={cardClasses}
      onClick={onClick}
      {...props}
    >
      {children}
    </div>
  );
};

